<template>
  <div class="group-students">
    <div>
      <div v-if="currentClassStudents.length === 0 && !queryString" class="no-learner-body">
        <user-icon />
        <h2 class="no-item-title">{{ $t("student.theres_no_learner_in_class") }}</h2>
      </div>
      <div v-else class="content">
        <h3>{{ $t($i18nLabelsHelper(organizationType, "students")) }}</h3>
        <div class="search-and-invite">
          <el-form class="search-form">
            <elsa-search v-model="queryString" @search="searchStudents" />
          </el-form>
          <invite-learner-modal
            :isOrganizationUseDummyEmail="false"
            v-if="canInviteLearner"
            @on-invited="$emit('goToTabIndex', 2)"
          />
          <invite-learner-modal
            v-if="canInviteLearner && isOrganizationUseDummyEmail"
            @on-invited="$emit('goToTabIndex', 2)"
          />
        </div>
        <div class="table-wrapper">
          <div class="options-wrapper">
            <div class="options">
              <el-form class="filter-form">
                <elsa-select
                  :options="filterOptions"
                  :placeholder="$t('classes.classes_details.students_section.assessment_filter')"
                  name="assessment-filter"
                  @change="changeAssessment"
                  v-model.lazy="assessmentType"
                  filterable
                />
              </el-form>
            </div>
            <div class="activity-reports">
              <h3>
                {{ $t("classes.classes_details.activity_reports.title") }}
              </h3>
              <div class="period-report">
                <date-interval-picker
                  :startDate="startDate"
                  :endDate="endDate"
                  :timelineLabel="timelineLabel"
                  @updatedDate="updatedDate"
                  :showScope="false"
                />
              </div>
              <div class="export-report">
                <class-excel-exporter
                  :startDate="startDate"
                  :canExport="canExport"
                  :endDate="endDate"
                  :assessmentType="assessmentType"
                />
              </div>
            </div>
          </div>
          <el-table
            :data="currentClassStudents"
            style="width: 100%"
            :empty-text="$t('common.no_values')"
            @cell-mouse-enter="showHistoryButton"
          >
            <user-info-table-column
              sortable
              prop="composed"
              :label="$t($i18nLabelsHelper(organizationType, 'student_name'))"
            />
            <base-table-column
              sortable
              :prop="assessmentTestField"
              align="center"
              :formatter="percentageFormatter"
              :showButton="true"
              :currIdHovering="currIdHovering"
              labelButton="History"
              @handleShowAssessment="handleShowAssessment"
              :label="$t('classes.classes_details.students_section.assessment_test')"
              :render-header="handleHelpTooltip"
            />
            <base-table-column
              sortable
              dataType="date"
              prop="last_active"
              align="center"
              dateFormat="HH:mma, MM/dd/yyyy"
              :label="$t('classes.classes_details.students_section.last_active_practice')"
            />
            <base-table-column
              sortable
              prop="time_spent"
              align="center"
              :formatter="intervalToHours"
              :label="$t('classes.classes_details.students_section.min_spent')"
            />
            <base-table-column
              sortable
              prop="lesson_count"
              align="center"
              :label="$t('classes.classes_details.students_section.lesson_count')"
            />
            <base-table-column
              sortable
              prop="last_eps"
              align="center"
              :formatter="percentageFormatter"
              :sort-method="sortScore"
              :showButton="true"
              :currIdHovering="currIdHovering"
              labelButton="Detail"
              @handleShowLessonsDetail="handleShowLessonsDetail"
              :label="$t('classes.classes_details.students_section.last_eps')"
            />
            <base-table-column
              sortable
              prop="time_avg_daily"
              align="center"
              :label="$t('classes.classes_details.students_section.avg_daily_use')"
            />
          </el-table>
          <div class="pagination">
            <pagination
              :page-size="maxNumberOfElements"
              :total="totalPage"
              :pager-counter="5"
              :current-page.sync="currentPage"
              v-on:current-change="updateCurrentPage"
            />
            <over-table-pagination
              class="over-table-pagination-right"
              :total="totalPage"
              :currentPage="currentPage"
              :pageSize="maxNumberOfElements"
            />
          </div>
        </div>
      </div>
    </div>
    <assessments-history
      :title="$t('common.assessment-history')"
      :dialogVisible="showAssessmentModal"
      @closed="showAssessmentModal = false"
      :assessments="assessmentsUser"
      :assessmentType="assessmentType"
    />
    <lessons-detail
      :title="$t('common.lesson_detail')"
      :dialogVisible="showLessonDetailModal"
      @closed="showLessonDetailModal = false"
      :lessons="lessonsDetailUser"
    />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import UserInfoTableColumn from "@/components/classes/UserInfoTableColumn";
import OverTablePagination from "@/components/common/OverTablePagination";
import Pagination from "@/components/common/Pagination";
import { pathOr, is, toString, compose, invoker, propOr, lt, unless, isNil } from "ramda";
import { NUMBER_ELEMENTS_PER_PAGE } from "@/constants/generic";
import BaseTableColumn from "@/components/classes/BaseTableColumn";
import AssessmentsHistory from "@/components/modals/reports/AssessmentsHistory";
import LessonsDetail from "@/components/modals/reports/LessonsDetail";
import percentage from "@/components/common/mixins/percentage";
import DateIntervalPicker from "@/components/common/DateIntervalPicker";
import ClassExcelExporter from "@/components/common/ClassExcelExporter";
// import { format, startOfMonth, toDate, fromUnixTime } from "date-fns";
import { warningMessage } from "@/utils/messages";
import ElsaSearch from "@/components/common/Search";
import ElsaSelect from "@/components/common/Select";
import userIcon from "@/assets/images/inline.user-icon-big.svg";
import InviteLearnerModal from "@/components/modals/organizations/InviteLearnersModal.vue";

const padStart = compose(invoker(2, "padStart")(2, "0"), toString);

export default {
  data() {
    return {
      queryString: "",
      currentPage: 1,
      showAssessmentModal: false,
      showLessonDetailModal: false,
      currIdHovering: "",
      assessmentType: this.hasSpecialAssessmentTest ? "sgd-assessment" : "general",
      assessmentTestField: this.hasSpecialAssessmentTest ? "org_assessment_test" : "elsa_assessment_test",
    };
  },
  components: {
    UserInfoTableColumn,
    OverTablePagination,
    Pagination,
    userIcon,
    BaseTableColumn,
    DateIntervalPicker,
    ClassExcelExporter,
    AssessmentsHistory,
    LessonsDetail,
    ElsaSearch,
    ElsaSelect,
    InviteLearnerModal,
  },
  props: [
    "organizationType",
    "startDate",
    "endDate",
    "scope",
    "timelineLabel",
    "filterOptions",
    "hasSpecialAssessmentTest",
    "groupId",
  ],
  computed: {
    ...mapGetters("classes", ["getCurrentClass"]),
    ...mapState("classes", ["assessmentsUser", "lessonsDetailUser"]),
    ...mapGetters({
      userIsTeacher: "organization/isOrganizationTeacher",
      userIsAdmin: "organization/isOrganizationAdmin",
      userIsAssociateAdmin: "organization/isOrganizationAssociateAdmin",
      isOrganizationUseDummyEmail: "organization/isOrganizationUseDummyEmail",
    }),
    currentClassStudents() {
      return pathOr([], ["students", "results"])(this.getCurrentClass);
    },
    maxNumberOfElements() {
      return NUMBER_ELEMENTS_PER_PAGE;
    },
    totalPage() {
      return pathOr(0, ["students", "count"])(this.getCurrentClass);
    },

    canExport() {
      return (
        this.$route.name == "class-details" && (this.userIsAdmin || this.userIsAssociateAdmin || this.userIsTeacher)
      );
    },
    startDateAndendDate() {
      return `${this.startDate}|${this.endDate}`;
    },
    canInviteLearner() {
      return this.userIsAdmin || this.userIsAssociateAdmin;
    },
  },
  methods: {
    ...mapActions("classes", [
      "getCurrentClassUsers",
      "getAssessmentUser",
      "getLessonsDetailUser",
      "resetAssessmentUser",
    ]),
    // eslint-disable-next-line no-unused-vars
    intervalToHours(row, column, cellValue, index) {
      if (!is(Number, cellValue)) {
        return "00:00:00";
      }
      const hours = Math.floor(cellValue / (60 * 60));
      const minutes = Math.floor((cellValue - hours * 3600) / 60);
      const seconds = cellValue - hours * 3600 - minutes * 60;

      return `${padStart(hours)}:${padStart(minutes)}:${padStart(seconds)}`;
    },
    percentageFormatter(row, column, cellValue, index) {
      if (cellValue == 0 || (column["property"] == "last_eps" && !row.time_spent)) return null;
      return percentage.filters.toPercentage(unless(isNil, Math.round)(cellValue));
    },
    sortScore(row1, row2) {
      const isNullEps = (row2.time_spent && row2["last_eps"] != null) - (row1.time_spent && row1["last_eps"] != null);
      return isNullEps || row2["last_eps"] - row1["last_eps"];
    },
    gotToStudentDetails(row) {
      const studentId = row.id;
      const organizationId = this.$store.getters["organization/currentOrganizationId"];
      return this.$router.push({
        name: "student-details",
        params: {
          studentId,
          organizationId,
          backTo: () => {
            return this.$router.push({
              name: "class-details",
              params: {
                classId: this.getCurrentClass.id,
                activeComponentProp: "class-students",
              },
            });
          },
        },
      });
    },
    showHistoryButton(row) {
      this.currIdHovering = row.id;
    },
    updateCurrentPage(page) {
      this.currentPage = page;
      this.getCurrentClassUsers({
        from: this.startDate,
        to: this.endDate,
        page,
        classId: this.groupId,
      });
    },
    updatedDate(dateObject) {
      this.$emit("updatedDate", dateObject);
    },
    async handleShowAssessment(student) {
      this.showAssessmentModal = true;
      this.resetAssessmentUser();
      try {
        await this.getAssessmentUser({
          student: student,
          from: this.startDate,
          to: this.endDate,
          assessment_type: this.assessmentType,
        });
      } catch {
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    async handleShowLessonsDetail(student) {
      this.showLessonDetailModal = true;
      try {
        await this.getLessonsDetailUser({
          student: student,
          from: this.startDate,
          to: this.endDate,
        });
      } catch {
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    handleHelpTooltip(h, { column }) {
      return h("span", {}, [
        h(
          "el-tooltip",
          {
            attrs: {
              content: this.columnTooltipMap()[column.property],
              effect: "dark",
              placement: "top",
            },
          },
          [h("span", {}, [column.label])]
        ),
      ]);
    },
    columnTooltipMap() {
      return {
        assessment_test: this.$t("information.assessment_test_col"),
      };
    },
    searchStudents(search) {
      return this.getCurrentClassUsers({
        from: this.startDate,
        to: this.endDate,
        search,
        classId: this.groupId,
      });
    },
    changeAssessment(assessment) {
      this.assessmentType = assessment;
      if (assessment == "sgd-assessment") this.assessmentTestField = "org_assessment_test";
      else if (assessment == "general") this.assessmentTestField = "elsa_assessment_test";
      else this.assessmentTestField = "mini_assessment_test";
    },
  },
  async mounted() {
    try {
      await this.getCurrentClassUsers({
        from: this.startDate,
        to: this.endDate,
        classId: this.groupId,
      });
      // this.getPendingInviteUsers()
    } catch (error) {
      error;
    }
  },
  watch: {
    // pendingUsersCurrentPage(page) {
    //   // return this.getPendingInviteUsers({ page })
    // },
    startDateAndendDate: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          return this.getCurrentClassUsers({
            from: this.startDate,
            to: this.endDate,
            classId: this.groupId,
          });
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../scss/_colors.scss";
.group-students {
  margin-top: 38px;
}
.content {
  margin-top: 24px;
  .table-wrapper {
    margin-top: 20px;
  }
}
::v-deep .search-box.elsa-input {
  .el-input .el-input__inner {
    padding-left: 15px;
  }
}

::v-deep .elsa-input .el-select .el-input__inner {
  width: 250px;
}

::v-deep .el-collapse {
  border: none;
  .el-collapse-item__header {
    min-width: 160px;
    // width: 164px;
    background: linear-gradient(180deg, #29f4ff -46.11%, #5257ff 151.97%);
    box-shadow: 0px 8px 18px rgba(69, 137, 255, 0.26);
    border-radius: 50px;
    font-weight: bold;
    font-size: 14px;
    color: white;
    padding: 0 10px;
    .button {
      width: 100%;
      i {
        font-size: 18px;
        vertical-align: text-top;
        margin-right: 2px;
      }
    }
  }
  .el-collapse-item__wrap {
    position: absolute;
    z-index: 2;
    margin-top: 8px;
    box-shadow: 0px 4px 7px rgba(4, 33, 50, 0.1);
    padding: 18px 16px;
  }
  .el-collapse-item__content {
    padding-bottom: 0;
    span {
      display: flex;
      font-weight: 600;
      font-size: 14px;
      color: $purple;
      cursor: pointer;
      i {
        margin-right: 10px;
      }
    }
  }
}

.invite-users {
  margin-top: 20px;
}

.invite-container {
  max-width: 906px;
  margin: 0 auto;
}

.options-wrapper {
  display: flex;
  height: fit-content;
  justify-content: space-between;
  .activity-reports {
    display: inherit;
    .period-report {
      margin: 0px 20px;
    }
  }
}
.no-learner-body {
  padding: 40px;
  text-align: center;
}
.no-item-title {
  font-weight: 600;
  font-size: 32px;
  text-align: center;
}
.no-item-desc {
  font-size: 18px;
}
.search-and-invite {
  display: flex;
  flex-direction: row;
  column-gap: 1rem;
}
::v-deep .invite-single-learner {
  height: 42px;
  font-size: 16px;
}
</style>
